import { Box, Button, Card, CardContent, CardHeader, IconButton, Input, InputAdornment, Tooltip, Dialog, DialogActions, DialogTitle, FormGroup, FormControlLabel, Switch, Select, MenuItem, Checkbox, ListItemText, Grid } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import TableFilter from "../../components/shared/TableFilter";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { sportsTeamServices } from "../../services/AllServices";
import { STATUS_OPTIONS_LIST } from "../../utils/constants";
import { Loader } from "../../components/CustomLoader";
import DeleteIcon from "@mui/icons-material/Delete";
import errorHandler from "../../utils/errorHandler";
import { EditOutlined } from "@mui/icons-material";
import DataTable from "react-data-table-component";
import CloseIcon from "@mui/icons-material/Close";
import { capitalize } from "../../utils/helpers";
import { fDate } from "../../utils/formatTime";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import Swal from "sweetalert2";

export default function SportsTeamManagement() {
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [show, setShow] = useState(false);
  const [warning, setWarning] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);

  const fetchUsers = async (query) => {
    const body = {
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await sportsTeamServices.getAll(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.teams);
          setTotalPages(res?.data?.data?.totalTeams);
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchUsers(query), 400),
    [currentPage, pageSize, status]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handleStatusChange = (event) => {
    const {
      target: { value },
    } = event;
    setStatus(typeof value === "string" ? value.split(",") : value);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const deleteData = async (id) => {
    Swal.fire({
      text: "Are you sure you want to delete this Team?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await sportsTeamServices.deleteTicket({ teamId:id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            setData((prevData) => prevData.filter((item) => item._id !== id));
            setTotalPages((prevTotal) => prevTotal - 1);
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const res = await sportsTeamServices.changeStatus({
        teamId:id,
        status: newStatus,
      });

      if (res.status === 200) {
        toast.success(res?.data?.message, { toastId: "success" });
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  useEffect(() => {
    fetchUsers(searchItem);
  }, [currentPage, pageSize, status, refresh]);

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      name: "Team Name",
      sortable: true,
      selector: (row) => capitalize(row.name),
    },
    // {
    //   name: "Category",
    //   sortable: true,
    //   selector: (row) => capitalize(row.categoryId?.name),
    // },
    {
      name: "CreatedAt",
      sortable: true,
      selector: (row) => fDate(row.createdAt),
    },
    {
      name: "Status",
      cell: (row) => (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={row.status === "active"}
                onClick={() => {
                  setShow(true);
                  setCurrentStatus(row.status);
                  setStatusId(row._id);
                  setWarning(
                    `Are you sure you want to ${row.status === "active" ? "deactivate" : "activate"
                    } this ticket?`
                  );
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />
        </FormGroup>
      ),
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/sports-team-management/view/${row?._id}`);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit" placement="left">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/sports-team-management/edit/${row?._id}`);
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" placement="top">
            <IconButton color="primary" onClick={() => deleteData(row._id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader title="Sports Team Management" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          variant: "contained",
          justifyContent: "flex-end",
        }}
      >

        <TableFilter
          data={STATUS_OPTIONS_LIST}
          value={status}
          setValue={setStatus}
          filterName="Status"
        />
        <Input
          value={searchItem}
          placeholder="Search"
          onChange={handleSearchChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  setSearchItem("");
                  fetchUsers("");
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />

        <Button
          size="large"
          type="submit"
          variant="contained"
          sx={{ ml: 3, mr: 3 }}
          onClick={() => navigate("/sports-team-management/add")}
        >
          Add
        </Button>
      </Box>

      <CardContent>
        <DataTable
          customStyles={{
            headCells: { style: { fontSize: "15px", fontWeight: "bold" } },
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50]}
          onChangeRowsPerPage={(currentRowsPerPage) =>
            setPageSize(currentRowsPerPage)
          }
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          className="custom"
        />
      </CardContent>

      <Dialog
        open={show}
        onClose={() => setShow(false)}
        className="popupbox-statis"
      >
        <DialogTitle>{warning}</DialogTitle>
        <DialogActions className="status-box-action">
          <Button
            variant="outlined"
            onClick={() => {
              toggleChecked(statusId, currentStatus);
              setShow(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShow(false)}
            color="primary"
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
